import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  PaperAirplaneIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const EmployeeChatPage = () => {
  const { user, apiCall } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [aiAssistMode, setAiAssistMode] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadCompanyChats();
    loadAvailableAgents();
  }, []);

  useEffect(() => {
    if (!activeChat?.id) {
      return undefined;
    }
  
    markChatAsRead(activeChat.id);
    loadChatMessages(activeChat.id);
  
    const interval = setInterval(() => {
      loadChatMessages(activeChat.id);
    }, 3000);
  
    return () => clearInterval(interval);
  }, [activeChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadCompanyChats = async () => {
    try {
      const response = await apiCall('/companies/chats');
      const companyChats = response?.data?.chats || [];
      setChats(response.success ? companyChats : []);
    } catch (error) {
      setChats([]);
    }
  };
  const markChatAsRead = async (chatId) => {
    if (!chatId || chatId.toString().startsWith("new-")) {
      return;
    }
  
    try {
      const response = await apiCall(`/chats/${chatId}/read`, {
        method: "POST",
      });
  
      console.log("COMPANY MARK CHAT AS READ RESPONSE:", response);
  
      setChats((prev) =>
        prev.map((chat) =>
          String(chat.id) === String(chatId)
            ? {
                ...chat,
                unread_count: 0,
              }
            : chat,
        ),
      );
  
      setActiveChat((prev) =>
        prev && String(prev.id) === String(chatId)
          ? {
              ...prev,
              unread_count: 0,
            }
          : prev,
      );
  
      window.dispatchEvent(new Event("zootel-chat-unread-refresh"));
  
      await loadCompanyChats();
    } catch (error) {
      console.error("Company mark chat as read error:", error);
    }
  };

  const loadAvailableAgents = async () => {
    try {
      const response = await apiCall('/ai/agents');
      if (response.success) {
        setAvailableAgents(response.data.agents || []);
        const supportAgent = response.data.agents?.find(agent => agent.key === 'customer_support');
        if (supportAgent) setSelectedAgent(supportAgent);
      }
    } catch (error) {
      console.error('Failed to load AI agents');
    }
  };

  const loadChatMessages = async (chatId) => {
    try {
      const response = await apiCall(`/chats/${chatId}/messages`);
      if (response.success) {
        setMessages(response.data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const sendMessage = async (messageText, senderType = 'employee') => {
    if (!messageText.trim() || !activeChat) return;

    setIsLoading(true);
    try {
      // Добавляем недостающие поля, которые требует SendMessageRequest на бэкенде
      const response = await apiCall(`/chats/${activeChat.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: activeChat.id,      // ID чата
          message_text: messageText.trim(),
          sender_type: senderType,     // 'employee'
          sender_id: user?.id,         // ID текущего сотрудника из контекста
        }),
      });

      if (response.success) {
        setNewMessage(''); // Очищаем поле ввода при успехе
        await loadChatMessages(activeChat.id);
        await loadCompanyChats();
      }
    } catch (error) {
      console.error('Failed to send message', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !activeChat?.id) return; 
  
    const messageText = newMessage.trim();
    setNewMessage(''); 
  
    try {
      const response = await apiCall(`/chats/${activeChat.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: activeChat.id,      
          message_text: messageText,  
          sender_type: 'employee',    // Обязательно для этой страницы
          sender_id: user.id          
        }),
      });
  
      if (response.success) {
        // ИСПРАВЛЕНО: имя функции загрузки должно быть loadChatMessages
        await loadChatMessages(activeChat.id); 
      }
    } catch (error) {
      console.error("Send error:", error);
      setNewMessage(messageText);
    }
  };

  const handleAIAssist = async () => {
    if (!aiPrompt.trim()) return;
    setIsLoading(true);
    setTimeout(async () => {
        await sendMessage(`[AI Response to: ${aiPrompt}] Это демонстрация ответа.`, 'ai');
        setAiPrompt('');
        setAiAssistMode(false);
        setIsLoading(false);
    }, 1000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-[100%] text-zinc-900 font-sans flex flex-col overflow-hidden">      
      <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 pb-6 overflow-hidden">
        <div className="bg-white border border-zinc-100 rounded-[32px] overflow-hidden shadow-sm flex h-full">
          
          <div className="w-[360px] border-r border-zinc-100 flex flex-col bg-zinc-50/30">
            <div className="p-5 border-b border-zinc-100">
              <div className="relative group">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input 
                  type="text" 
                  placeholder="Поиск..." 
                  className="w-full bg-zinc-100 border-none rounded-xl py-2 pl-9 pr-4 text-sm outline-none focus:ring-1 focus:ring-zinc-200 transition-all"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto py-3 custom-scrollbar">
            {chats.map((chat) => {
  const isActive = String(activeChat?.id) === String(chat.id);

  return (
    <div
      key={chat.id}
      onClick={() => {
        setActiveChat(chat);
        markChatAsRead(chat.id);
      }}
      className={`mx-3 mb-1 p-4 rounded-[20px] cursor-pointer transition-all ${
        isActive
          ? "bg-zinc-900 text-white shadow-lg shadow-zinc-200 scale-[1.02]"
          : "hover:bg-white hover:shadow-sm text-zinc-900"
      }`}
    >
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-bold truncate text-[14px]">
          {chat.user_name || `User ${chat.user_id?.slice(0, 6) || ""}`}
        </h3>

        {Number(chat.unread_count || 0) > 0 && !isActive && (
          <span className="bg-zinc-900 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full ring-2 ring-white">
            {chat.unread_count}
          </span>
        )}
      </div>

      <p
        className={`text-[12px] truncate font-medium ${
          isActive ? "text-zinc-400" : "text-zinc-500"
        }`}
      >
        {chat.last_message?.message_text || "Нет сообщений"}
      </p>
    </div>
  );
})}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            {activeChat ? (
              <>
                <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6 bg-zinc-50/20 custom-scrollbar">
                  {messages.map((message) => {
                    const isUser = message.sender_type === 'user';
                    return (
                      <div key={message.id} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                        <div className={`flex flex-col ${isUser ? 'items-start' : 'items-end'} max-w-[70%]`}>
                          <div className="flex items-center space-x-2 mb-1 px-1">
                            <span className="text-[9px] font-black text-zinc-300 uppercase tracking-widest">
                              {formatTime(message.created_at)}
                            </span>
                          </div>
                          <div className={`px-4 py-2.5 rounded-[20px] text-[13.5px] leading-relaxed shadow-sm ${
                            isUser ? 'bg-white border border-zinc-100 text-zinc-900 rounded-tl-none' : 
                            'bg-zinc-900 text-white rounded-tr-none'
                          }`}>
                            {message.message_text}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* --- ФИКСИРОВАННАЯ КНОПКА ОТПРАВКИ --- */}
                <div className="p-5 bg-white border-t border-zinc-50 flex-none">
                  <form onSubmit={handleSendMessage} className="relative flex items-center bg-zinc-100 rounded-[20px] p-1.5 pr-2 focus-within:ring-1 focus-within:ring-zinc-200 transition-all">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Ответить..."
                      className="flex-1 bg-transparent border-none py-2 px-4 text-sm outline-none focus:ring-0"
                    />
                    <button 
                      type="submit" 
                      disabled={!newMessage.trim() || isLoading} 
                      className="h-9 w-10 bg-zinc-900 text-white rounded-xl flex items-center justify-center shadow-lg shadow-zinc-200 active:scale-90 transition-transform disabled:bg-zinc-300"
                    >
                      {isLoading ? (
                        <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      ) : (
                        <PaperAirplaneIcon className="w-4 h-4 text-white transform -rotate-45 -translate-y-0.5 translate-x-0.5" />
                      )}
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-zinc-50/10">
                <p className="text-[11px] font-black text-zinc-300 uppercase tracking-[0.2em]">Выберите диалог</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeChatPage;